import api from './api';

export interface DocumentReference {
  id: string;
  title: string;
  type: string;
  typeCode: string;
  category: string;
  status: string;
  date: string;
  author: string | null;
  signed: boolean;
  signedBy: string | null;
  version: number;
  contentType?: string;
  contentUrl?: string;
  description?: string;
}

export interface CreateDocumentPayload {
  patientId: string;
  title: string;
  typeCode?: string;
  typeDisplay?: string;
  category?: string;
  description?: string;
  contentType: string;
  contentUrl: string;
  contentSize: number;
  contentHash?: string;
  encounterId?: string;
  authorId?: string;
}

export const DOCUMENT_TYPES = [
  { code: '11506-3', display: 'Progress Note' },
  { code: '11488-4', display: 'Consultation Note' },
  { code: '34117-2', display: 'History & Physical' },
  { code: '18842-5', display: 'Discharge Summary' },
  { code: '59284-0', display: 'Informed Consent' },
  { code: '11502-2', display: 'Laboratory Report' },
  { code: '28570-0', display: 'Procedure Note' },
  { code: '57133-1', display: 'Referral Note' },
  { code: 'OTHER', display: 'Other' },
] as const;

export const ALLOWED_DOCUMENT_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
];

export const MAX_DOCUMENT_SIZE = 15 * 1024 * 1024; // 15MB

export const documentService = {
  async getPatientDocuments(
    clinicId: string,
    patientId: string,
    filters?: { status?: string; type?: string }
  ) {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.type) params.set('type', filters.type);
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`/emr/${clinicId}/patients/${patientId}/documents${query}`);
    return response.data.data;
  },

  async getDocument(clinicId: string, documentId: string) {
    const response = await api.get(`/emr/${clinicId}/documents/${documentId}`);
    return response.data.data;
  },

  async getDocumentUploadUrl(filename: string, contentType: string) {
    const response = await api.post('/uploads/presigned-url', {
      filename,
      contentType,
      folder: 'patient-documents',
    });
    return response.data.data as { uploadUrl: string; key: string };
  },

  async createDocument(clinicId: string, data: CreateDocumentPayload) {
    const response = await api.post(`/emr/${clinicId}/documents`, data);
    return response.data.data;
  },

  async signDocument(clinicId: string, documentId: string, practitionerId?: string) {
    const response = await api.post(`/emr/${clinicId}/documents/${documentId}/sign`, {
      practitionerId,
    });
    return response.data.data;
  },

  async getDocumentViewUrl(s3Key: string) {
    const response = await api.get(`/uploads/view-url?key=${encodeURIComponent(s3Key)}`);
    return response.data.data.viewUrl as string;
  },
};
